import { ArrowLeft, ArrowRight } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '../../../components/ui/card'
import { cn } from '../../../lib/utils'

interface WizardChromeProps {
  eyebrow: string
  title: string
  description: string
  children: React.ReactNode
  onBack?: () => void
  onContinue?: () => void
  continueLabel?: string
  continueDisabled?: boolean
  className?: string
}

export function WizardChrome({
  eyebrow,
  title,
  description,
  children,
  onBack,
  onContinue,
  continueLabel = 'Continue',
  continueDisabled,
  className
}: WizardChromeProps) {
  return (
    <Card className={cn('gap-0 py-0 shadow-sm', className)}>
      <CardHeader className="gap-1.5 px-6 pt-5 pb-1">
        <p className="font-mono text-11 tracking-[0.08em] text-ink-3 uppercase">{eyebrow}</p>
        <CardTitle className="text-lg font-semibold text-ink">{title}</CardTitle>
        <CardDescription className="max-w-[66ch] text-13 text-ink-3">{description}</CardDescription>
      </CardHeader>
      <CardContent className="px-6 pt-3 pb-2">{children}</CardContent>
      {(onBack || onContinue) && (
        <CardFooter className="w-fit self-end gap-2 rounded-b-xl px-6 pt-1 pb-4">
          {onBack ? (
            <Button type="button" size="sm" variant="outline" onClick={onBack}>
              Back
            </Button>
          ) : null}
          {onContinue ? (
            <Button type="button" size="sm" disabled={continueDisabled} onClick={onContinue}>
              {continueLabel}
              <ArrowRight className="size-4" />
            </Button>
          ) : null}
        </CardFooter>
      )}
    </Card>
  )
}

interface WizardPageHeaderProps {
  title: string
  subtitle: string
  stepCounter?: string
  onBackToList?: () => void
  actions?: React.ReactNode
}

export function WizardPageHeader({
  title,
  subtitle,
  stepCounter,
  onBackToList,
  actions
}: WizardPageHeaderProps) {
  return (
    <header className="flex flex-wrap items-end gap-4">
      <div className="min-w-0 flex-1">
        {onBackToList ? (
          <button
            type="button"
            onClick={onBackToList}
            className="mb-1.5 inline-flex items-center gap-1.5 border-0 bg-transparent p-0 text-xs font-medium text-ink-3 hover:text-brand"
          >
            <ArrowLeft className="size-3.5" strokeWidth={2} />
            Ingest
          </button>
        ) : null}
        <h1 className="m-0 text-xl font-semibold tracking-tight text-ink">{title}</h1>
        <p className="mt-1 text-13 text-ink-3">{subtitle}</p>
      </div>
      {stepCounter ? (
        <div className="shrink-0 font-mono text-11 tracking-[0.08em] text-ink-3 uppercase">
          {stepCounter}
        </div>
      ) : null}
      {actions}
    </header>
  )
}
